import { RowCrudStatus } from "../../common/types";
import {
  WorkAuthor,
  WorkAuthor_CreateInput,
  WorkAuthor_UpdateInput,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { authorComposerDisplayId } from "../../authorComposer/store/AuthorComposerCommonStore";
import {
  WorkAuthorPublisherValuesType,
  getWorkAuthorPublisherValues,
  getWorkAuthorPublisherCreateInput,
  getWorkAuthorPublisherUpdateInput,
} from "../../workAuthorPublisher/store/WorkAuthorPublisherCommonStore";
import {
  addedStatusList,
  originalDeletedStatusList,
  originalUpdatedStatusList,
} from "../../common/types";

export type WorkAuthorValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  __author: string;
  showAuthor: { displayId: string; id: string } | null;
  lyricsPercentage: number;
  musicPercentage: number;
  showPublishers: WorkAuthorPublisherValuesType[];
};

export type WorkAuthorErrorsType = {
  author?: { message: string; fieldLabel: string };
  lyricsPercentage?: { message: string; fieldLabel: string };
  musicPercentage?: { message: string; fieldLabel: string };
  publishers?: { message: string; fieldLabel: string };
};

export function WorkAuthorValuesInit(): WorkAuthorValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    __author: "",
    showAuthor: null,
    lyricsPercentage: 0,
    musicPercentage: 0,
    showPublishers: [],
  };
}

export type WorkAuthorType = WorkAuthor | WorkAuthorValuesType;

export function workAuthorDisplayId(
  source: Partial<WorkAuthor>,
  _langStore: LangStore
) {
  return (
    (source.author
      ? authorComposerDisplayId(source.author, _langStore)
      : _langStore.emptyStringSymbol) +
    " L:" +
    (source.lyricsPercentage?.toString() ?? _langStore.emptyStringSymbol) +
    "% - M: " +
    (source.musicPercentage?.toString() ?? _langStore.emptyStringSymbol) +
    "%"
  );
}

export function getWorkAuthorValues(
  source: WorkAuthorType,
  _langStore: LangStore,
  _duplicated: boolean = false
): WorkAuthorValuesType {
  const entityValues = WorkAuthorValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : workAuthorDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.showAuthor =
    "author" in source && source.author
      ? {
          id: source.author.id,
          displayId: authorComposerDisplayId(source.author, _langStore),
        }
      : "showAuthor" in source && source.showAuthor
      ? { id: source.showAuthor.id, displayId: source.showAuthor.displayId }
      : null;
  entityValues.__author = entityValues.showAuthor?.displayId || "";
  entityValues.lyricsPercentage = Number(source.lyricsPercentage) || 0;
  entityValues.musicPercentage = Number(source.musicPercentage) || 0;
  entityValues.showPublishers =
    "publishers" in source && source.publishers
      ? (source.publishers || []).map((x) => {
          const row: WorkAuthorPublisherValuesType =
            getWorkAuthorPublisherValues(x, _langStore, _duplicated);
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showPublishers" in source && source.showPublishers
      ? (source.showPublishers || []).map((x) => {
          const row: WorkAuthorPublisherValuesType =
            getWorkAuthorPublisherValues(x, _langStore, _duplicated);
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getWorkAuthorUpdateInput(
  values: WorkAuthorValuesType
): WorkAuthor_UpdateInput {
  return {
    id: values.id,
    version_: values.version_,
    authorId: values.showAuthor?.id || "",
    lyricsPercentage: values.lyricsPercentage,
    musicPercentage: values.musicPercentage,
    publishers: {
      create: values.showPublishers
        .filter((x) => addedStatusList.includes(x.__status__))
        .map((x) => getWorkAuthorPublisherCreateInput(x)),
      update: values.showPublishers
        .filter((x) => originalUpdatedStatusList.includes(x.__status__))
        .map((x) => getWorkAuthorPublisherUpdateInput(x)),
      remove: values.showPublishers
        .filter((x) => originalDeletedStatusList.includes(x.__status__))
        .map((x) => x.id),
    },
  };
}

export function getWorkAuthorCreateInput(
  values: WorkAuthorValuesType
): WorkAuthor_CreateInput {
  return {
    authorId: values.showAuthor?.id || "",
    lyricsPercentage: values.lyricsPercentage,
    musicPercentage: values.musicPercentage,
    publishers: values.showPublishers
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getWorkAuthorPublisherCreateInput(x)),
  };
}
